import React, { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useAuth } from "react-oidc-context";
import { resolveRoleFromGroups, getPermissionsForRole } from "./rolePermissions";
import { ENABLE_RBAC } from "./featureFlags";

const AuthorizationContext = createContext(null);
AuthorizationContext.displayName = "AuthorizationContext";

AuthorizationProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthorizationProvider({ children }) {
  const { user } = useAuth();

  const value = useMemo(() => {
    // user is null while loading or unauthenticated — handle gracefully.
    const rawGroups = user?.profile?.["cognito:groups"];

    const role = resolveRoleFromGroups(rawGroups);
    const permissions = getPermissionsForRole(role);

    const hasPermission = (permission) => {
      // RBAC flag off → bypass all permission checks
      if (!ENABLE_RBAC) return true;
      return typeof permission === "string" && permissions.has(permission);
    };

    return {
      user,
      role,
      permissions,
      hasPermission,
    };
  }, [user]);

  return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>;
}

export function useAuthorizationContext() {
  const ctx = useContext(AuthorizationContext);

  if (ctx === null) {
    throw new Error(
      "[AV Sync RBAC] useAuthorization() was called outside of <AuthorizationProvider>.\n" +
        "Ensure <AuthorizationProvider> wraps your <App /> inside <AuthProvider>."
    );
  }

  return ctx;
}
